import { Job, SupabaseJob, Resume, MatchResponse, KeywordMatch, SupabaseResumeContent } from '@applicator/shared'
import { useState, useEffect } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'

import { matchAts } from '../lib/api/atsClient'
import { supabase } from '../lib/supabase'
import { getSelectedTargetTitle } from '../utils/targetTitleUtils'

const KEYWORD_FOUND_STYLE = 'bg-green-100 text-green-800'
const KEYWORD_WEAK_STYLE = 'bg-amber-100 text-amber-800'
const KEYWORD_MISSING_STYLE = 'bg-red-100 text-red-800'

type Step = 'select-resume' | 'analyzing' | 'results' | 'editing'

const STEP_SELECT_RESUME: Step = 'select-resume'

function PrepareApplication() {
  const { jobId } = useParams<{ jobId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const jobIdsParam = searchParams.get('jobIds')
  const jobIds = jobId ? [jobId] : (jobIdsParam ? jobIdsParam.split(',') : [])

  const [step, setStep] = useState<Step>(STEP_SELECT_RESUME)
  const [job, setJob] = useState<Job | null>(null)
  const [supabaseJob, setSupabaseJob] = useState<SupabaseJob | null>(null)
  const [resumes, setResumes] = useState<Resume[]>([])
  const [selectedResumeId, setSelectedResumeId] = useState<string | null>(null)
  const [resumeText, setResumeText] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [, setAnalyzing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [matchResult, setMatchResult] = useState<MatchResponse | null>(null)

  // Load job
  useEffect(() => {
    if (jobIds.length === 0) {
      setError('No job ID provided')
      setLoading(false)
      return
    }

    const loadJob = async () => {
      try {
        setLoading(true)
        setError(null)

        const { data: { user } } = await supabase.auth.getUser()
        if (!user) {
          setError('User not authenticated')
          return
        }

        const { data, error: fetchError } = await supabase
          .from('jobs')
          .select('*')
          .eq('id', jobIds[0])
          .eq('user_id', user.id)
          .single()

        if (fetchError) throw fetchError

        // Map to Job type
        const mappedJob: Job = {
          id: data.id,
          jobNo: data.job_no,
          position: data.title,
          company: data.company,
          location: data.location,
          salary: data.salary_max ? `${data.salary_max} ${data.salary_currency || ''}` : null,
          status: data.status,
          dateSaved: data.saved_at,
          dateApplied: data.applied_at,
          followUp: data.follow_up_at,
          deadline: data.deadline_at,
          excitement: data.excitement,
        }

        setJob(mappedJob)
        setSupabaseJob(data as SupabaseJob)
      } catch (err) {
        console.error('Error loading job:', err)
        setError(err instanceof Error ? err.message : 'Failed to load job')
      } finally {
        setLoading(false)
      }
    }

    loadJob()
  }, [jobIds])

  // Load resumes
  useEffect(() => {
    const loadResumes = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) return

        const { data, error: fetchError } = await supabase
          .from('resumes')
          .select('*')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false })

        if (fetchError) throw fetchError

        const formattedResumes: Resume[] = (data || []).map((r: { id: string; title: string; created_at: string; updated_at: string }) => ({
          id: r.id,
          title: r.title,
          createdAt: r.created_at,
          updatedAt: r.updated_at,
        }))

        setResumes(formattedResumes)
        
        // Auto-select first resume if only one
        if (formattedResumes.length === 1) {
          setSelectedResumeId(formattedResumes[0].id)
        }
      } catch (err) {
        console.error('Error loading resumes:', err)
      }
    }

    loadResumes()
  }, [])

  // Load resume text when resume is selected
  useEffect(() => {
    if (!selectedResumeId || step !== STEP_SELECT_RESUME) return

    const loadResumeText = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('resume_content')
          .select('*')
          .eq('resume_id', selectedResumeId)
          .order('display_order', { ascending: true })

        if (fetchError) throw fetchError

        // Convert resume data to text format for ATS matching
        // This is a simplified version - you might want to create a proper formatter
        const sections: string[] = []
        
        data?.forEach((item: SupabaseResumeContent) => {
          const content = item.content
          if (item.section_type === 'contact_info' && content && typeof content === 'object' && !Array.isArray(content)) {
            const contactInfo = content as Record<string, unknown>
            const name = [contactInfo.firstName, contactInfo.middleName, contactInfo.lastName].filter(Boolean).join(' ')
            if (name) sections.push(name)
            if (contactInfo.email) sections.push(`Email: ${contactInfo.email}`)
            if (contactInfo.phone) sections.push(`Phone: ${contactInfo.phone}`)
            if (contactInfo.location) sections.push(`Location: ${contactInfo.location}`)
          } else if (item.section_type === 'target_title' && Array.isArray(content)) {
            const title = getSelectedTargetTitle(content)
            if (title) sections.push('TARGET TITLE: ' + title)
          } else if (item.section_type === 'professional_summary' && Array.isArray(content)) {
            const summaries = content.map((s: Record<string, unknown>) => (s.text as string) || '').filter(Boolean)
            if (summaries.length > 0) {
              sections.push('PROFESSIONAL SUMMARY: ' + summaries.join('\n'))
            }
          } else if (item.section_type === 'work_experience' && Array.isArray(content)) {
            content.forEach((company: Record<string, unknown>) => {
              if (company.companyName) {
                sections.push(`${company.companyName} - ${(company.jobTitle as string) || ''}`)
                if (Array.isArray(company.roles)) {
                  (company.roles as Array<Record<string, unknown>>).forEach((role: Record<string, unknown>) => {
                    if (role.description) sections.push(role.description as string)
                  })
                }
              }
            })
          } else if (item.section_type === 'skills' && Array.isArray(content)) {
            const skills = content.flatMap((cat: Record<string, unknown>) => 
              Array.isArray(cat.skills) ? (cat.skills as string[]) : []
            )
            if (skills.length > 0) {
              sections.push('SKILLS: ' + skills.join(', '))
            }
          }
        })

        setResumeText(sections.join('\n\n'))
      } catch (err) {
        console.error('Error loading resume text:', err)
        setError('Failed to load resume content')
      }
    }

    loadResumeText()
  }, [selectedResumeId, step])

  const handleAnalyze = async () => {
    if (!job || !supabaseJob || !selectedResumeId || !resumeText) {
      setError('Please select a resume and ensure job data is loaded')
      return
    }

    setAnalyzing(true)
    setError(null)
    setStep('analyzing')

    try {
      const jobText = supabaseJob.description || `${job.position} at ${job.company}\n\n${supabaseJob.description || ''}`

      const result = await matchAts({
        job_title: job.position,
        job_text: jobText,
        resume_text: resumeText,
      })

      setMatchResult(result)
      setStep('results')
    } catch (err) {
      console.error('Error analyzing:', err)
      setError(err instanceof Error ? err.message : 'Failed to analyze resume')
      setStep('select-resume')
    } finally {
      setAnalyzing(false)
    }
  }

  const handleStartEditing = () => {
    if (!selectedResumeId) return
    setStep('editing')
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  if (error && !job) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-red-500">Error: {error}</div>
        <button
          onClick={() => navigate('/')}
          className="ml-4 px-4 py-2 bg-gray-600 text-white rounded-lg"
        >
          Back to Jobs
        </button>
      </div>
    )
  }

  if (step === 'editing' && selectedResumeId) {
    // Redirect to resume detail page with the selected resume
    window.location.href = `/resume/${selectedResumeId}?jobId=${jobId || ''}&matchResult=${encodeURIComponent(JSON.stringify(matchResult))}`
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-gray-500">Redirecting to resume editor...</div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white">
      <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-gray-900"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-semibold text-gray-900">Prepare Application</h1>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {step === STEP_SELECT_RESUME && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Select Resume</h2>
              <p className="text-sm text-gray-600 mb-4">
                Choose a resume to match against: <strong>{job?.position}</strong> at <strong>{job?.company}</strong>
              </p>
            </div>

            {resumes.length === 0 ? (
              <div className="p-6 bg-gray-50 border border-gray-200 rounded-lg text-center">
                <p className="text-gray-600 mb-4">No resumes found. Create one first.</p>
                <button
                  onClick={() => navigate('/resumes')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Create Resume
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {resumes.map((resume) => (
                  <button
                    key={resume.id}
                    onClick={() => setSelectedResumeId(resume.id)}
                    className={`w-full p-4 text-left border-2 rounded-lg transition-colors ${
                      selectedResumeId === resume.id
                        ? 'border-green-600 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{resume.title}</div>
                    <div className="text-sm text-gray-500 mt-1">
                      Updated {new Date(resume.updatedAt).toLocaleDateString()}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {selectedResumeId && resumeText && (
              <div className="flex justify-end">
                <button
                  onClick={handleAnalyze}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                >
                  Analyze Match
                </button>
              </div>
            )}
          </div>
        )}

        {step === 'analyzing' && (
          <div className="max-w-3xl mx-auto text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Analyzing resume match...</p>
          </div>
        )}

        {step === 'results' && matchResult && (
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Match Analysis Results</h2>
                <div className={`text-4xl font-bold ${
                  matchResult.match_score >= 70
                    ? 'text-green-600'
                    : matchResult.match_score >= 40
                    ? 'text-amber-600'
                    : 'text-red-600'
                }`}>
                  {matchResult.match_score}/100
                </div>
              </div>

              <div className="space-y-6">
                {matchResult.hard_skills.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Hard Skills</h3>
                    <div className="flex flex-wrap gap-2">
                      {matchResult.hard_skills.map((keyword: KeywordMatch) => (
                        <span
                          key={keyword.keyword}
                          className={`px-3 py-1 rounded-md text-sm ${
                            keyword.status === 'found'
                              ? KEYWORD_FOUND_STYLE
                              : keyword.status === 'weak'
                              ? KEYWORD_WEAK_STYLE
                              : KEYWORD_MISSING_STYLE
                          }`}
                        >
                          {keyword.keyword} ({keyword.status})
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {matchResult.soft_skills.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Soft Skills</h3>
                    <div className="flex flex-wrap gap-2">
                      {matchResult.soft_skills.map((keyword: KeywordMatch) => (
                        <span
                          key={keyword.keyword}
                          className={`px-3 py-1 rounded-md text-sm ${
                            keyword.status === 'found'
                              ? KEYWORD_FOUND_STYLE
                              : keyword.status === 'weak'
                              ? KEYWORD_WEAK_STYLE
                              : KEYWORD_MISSING_STYLE
                          }`}
                        >
                          {keyword.keyword} ({keyword.status})
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {matchResult.other.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Other Keywords</h3>
                    <div className="flex flex-wrap gap-2">
                      {matchResult.other.map((keyword: KeywordMatch) => (
                        <span
                          key={keyword.keyword}
                          className={`px-3 py-1 rounded-md text-sm ${
                            keyword.status === 'found'
                              ? KEYWORD_FOUND_STYLE
                              : keyword.status === 'weak'
                              ? KEYWORD_WEAK_STYLE
                              : KEYWORD_MISSING_STYLE
                          }`}
                        >
                          {keyword.keyword} ({keyword.status})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={handleStartEditing}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                >
                  Edit Resume
                </button>
                <button
                  onClick={() => {
                    // TODO: Implement PDF export
                    alert('PDF export coming soon!')
                  }}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors"
                >
                  Export PDF
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PrepareApplication
